import { Download, ExternalLink, FileText, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

/**
 * One file, as this dialog needs it. Deliberately minimal so any caller can hand over what it
 * already has — the session artefact gallery, a template preview, a future document viewer.
 */
export interface PreviewableFile {
  /** Shown in the header. The applicant's own filename where we have one. */
  label: string;
  /** A presigned or blob URL. Never a data URI in practice — those are offloaded before storage. */
  url: string;
  /** PDFs render in an iframe, everything else as an image. */
  isPdf: boolean;
}

/**
 * THE file preview dialog — one modal for every file the product opens.
 *
 * WHY A MODAL AND NOT A NEW TAB. A signed storage URL opened with `target="_blank"` takes the
 * reviewer out of the session they are reviewing, loses their scroll position and accordion state,
 * and shows them the browser's own PDF chrome with a URL full of signature query parameters. Coming
 * back means finding the step again. Reviewing a document is part of reading the session, so it
 * belongs inside it.
 *
 * ONE COMPONENT FOR ALL TYPES, on purpose. Only PDFs need it today, but a viewer that handles just
 * one type is how a second, near-identical image lightbox gets written next to it — and then the two
 * disagree about the close key, the backdrop click and the mobile layout.
 *
 * MOBILE FIRST IN THE LAYOUT, not as an afterthought: full-screen under `sm`, a centred sheet above
 * it. A fixed-size modal is what breaks on a small phone — the header wraps, the actions fall off
 * the edge, and the document itself gets a viewport too short to read.
 */
export function FilePreviewDialog({
  file,
  onClose,
}: {
  /** The file to show, or null to render nothing. */
  file: PreviewableFile | null;
  onClose: () => void;
}) {
  /**
   * Escape closes, and the page behind does not scroll while this is open.
   *
   * Both are the behaviours people expect of a modal and both are easy to leave out. Body scroll
   * especially: without it a touch drag on the backdrop scrolls the session underneath, so closing
   * the dialog leaves the reviewer somewhere they did not choose to be.
   */
  useEffect(() => {
    if (!file) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [file, onClose]);

  if (!file) return null;

  return (
    // `p-0 sm:p-6` is the mobile-first half: edge to edge on a phone, inset on a desktop.
    <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-6">
      {/* The backdrop is a real BUTTON, not a div with an onClick.
          Click-to-dismiss is a genuine action, so it belongs on an element that can receive one —
          which also gets it keyboard focus and a name for free. `-z-10` keeps it behind the sheet,
          so a click inside the dialog never reaches it and the "did the click land on the backdrop"
          check that a single div needed disappears entirely. */}
      <button
        type="button"
        aria-label="Close preview"
        onClick={onClose}
        className="absolute inset-0 -z-10 cursor-default bg-black/60"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={file.label}
        className={cn(
          'flex flex-col overflow-hidden bg-white shadow-2xl',
          // Full-screen on a phone; a large centred sheet from `sm` up. `h-dvh` rather than
          // `h-screen` so the mobile browser's own chrome is accounted for — with `100vh` the footer
          // sits under the address bar on iOS.
          //
          // DELIBERATELY BIG: `max-w-3xl` × 90dvh left an A4 page rendered at about a third of its
          // real size, which is unreadable for the thing this dialog exists to do. A KYC form is a
          // dense portrait page and the reviewer is reading the small print on it.
          'h-dvh w-full rounded-none sm:h-[96dvh] sm:max-w-[min(96vw,1100px)] sm:rounded-2xl',
        )}
      >
        <header className="flex shrink-0 items-center gap-2 border-b border-[#E5E7EB] px-4 py-3">
          <FileText size={16} className="shrink-0 text-primary" />
          {/* `min-w-0` + `truncate`: a long filename must shorten rather than push the buttons off
              the edge, which is exactly how this header breaks on a narrow phone. */}
          <span className="min-w-0 flex-1 truncate text-[length:var(--text-14)] font-semibold text-[#041A3A]">
            {file.label}
          </span>

          {/* Download and open-in-tab are kept as SECONDARY actions. The modal is the default way
              in, but a reviewer who wants the file on disk, or the browser's own PDF tools, should
              not have to be denied them. Icon-only on a phone, where there is no room for labels. */}
          <a
            href={file.url}
            download={file.label}
            rel="noreferrer"
            title="Download"
            aria-label="Download"
            className="shrink-0 rounded-lg p-1.5 text-[#64748B] transition-colors hover:bg-[#F1F5F9] hover:text-primary"
          >
            <Download size={16} />
          </a>
          <a
            href={file.url}
            target="_blank"
            // `noreferrer` keeps the signed URL out of any Referer header.
            rel="noreferrer"
            title="Open in new tab"
            aria-label="Open in new tab"
            className="shrink-0 rounded-lg p-1.5 text-[#64748B] transition-colors hover:bg-[#F1F5F9] hover:text-primary"
          >
            <ExternalLink size={16} />
          </a>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            aria-label="Close"
            className="shrink-0 rounded-lg p-1.5 text-[#64748B] transition-colors hover:bg-[#F1F5F9] hover:text-destructive"
          >
            <X size={18} />
          </button>
        </header>

        {/* Keyed by URL so opening a second document REMOUNTS the body — which is what resets its
            loading state, instead of an effect that has to remember to. */}
        <PreviewBody key={file.url} file={file} />
      </div>
    </div>
  );
}

/**
 * How long a preview may sit on the spinner before we call it stalled and offer a way out.
 *
 * There is no reliable event for "the browser declined to render this inline". Mobile Safari and
 * several Android browsers refuse PDFs in an iframe outright, and some of them still fire `load`, so
 * waiting for an error that never arrives leaves a spinner turning over an empty box forever. A
 * grace period turns that dead end into two working buttons.
 */
const STALL_AFTER_MS = 6000;

/**
 * The document itself, plus its loading state.
 *
 * A SEPARATE COMPONENT so the parent can key it by URL: remounting is what resets the state when a
 * second file is opened, with no effect to keep in step. An effect doing the same job is one more
 * thing that can be given the wrong dependency.
 *
 * NO PDF "OPEN PARAMETERS" HERE, deliberately. Appending the Acrobat fragment
 * (`#toolbar=0&navpanes=0&scrollbar=0&view=FitH`) to hide the browser's own viewer chrome made
 * Chrome render a BLANK frame instead — it declines to draw at all with those parameters on a
 * cross-origin document. A preview that shows the file inside a toolbar we did not choose is worth
 * incomparably more than a tidy frame with nothing in it, and the toolbar's zoom and page controls
 * are useful for the dense forms this mostly opens. Size was the real complaint and the sheet above
 * fixes that. If the chrome ever has to go, the only honest way is a bundled renderer (pdf.js), not
 * a URL trick that varies per browser version.
 */
function PreviewBody({ file }: { file: PreviewableFile }) {
  const [state, setState] = useState<'loading' | 'ready' | 'stalled'>('loading');

  useEffect(() => {
    if (state !== 'loading') return;
    const timer = setTimeout(() => setState('stalled'), STALL_AFTER_MS);
    return () => clearTimeout(timer);
  }, [state]);

  return (
    <div className="relative min-h-0 flex-1 bg-[#F8FAFC]">
      {state === 'loading' && (
        <span className="absolute inset-0 flex items-center justify-center gap-2 text-[length:var(--text-13)] text-[#64748B]">
          <Loader2 size={16} className="animate-spin" />
          Loading…
        </span>
      )}

      {/* The way out when the browser will not show the file inline. Not an error — the file is
          fine, this browser just will not draw it here — so it reads as a choice, not a failure. */}
      {state === 'stalled' && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-[#F8FAFC] px-6 text-center">
          <FileText size={28} className="text-[#94A3B8]" />
          <p className="m-0 text-[length:var(--text-13)] text-[#64748B]">
            This browser can’t show the file here. Download it or open it in a new tab.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <a
              href={file.url}
              download={file.label}
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-[length:var(--text-13)] font-medium text-white transition-opacity hover:opacity-90"
            >
              <Download size={14} />
              Download
            </a>
            <a
              href={file.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#CBD5E1] bg-white px-3 py-2 text-[length:var(--text-13)] font-medium text-[#334155] transition-colors hover:bg-[#F1F5F9]"
            >
              <ExternalLink size={14} />
              Open in new tab
            </a>
          </div>
        </div>
      )}

      {file.isPdf ? (
        // An <iframe>, not <embed>/<object>: it is the one element every current browser renders a
        // PDF in, and unlike a JS viewer it needs no bundled dependency. Where a browser refuses,
        // the stalled fallback above takes over.
        <iframe
          src={file.url}
          title={file.label}
          onLoad={() => setState('ready')}
          className="h-full w-full border-0"
        />
      ) : (
        <img
          src={file.url}
          alt={file.label}
          onLoad={() => setState('ready')}
          onError={() => setState('stalled')}
          // `contain` so a portrait face crop and a landscape scan both fit whole — cropping is
          // never right in a viewer whose purpose is looking closely at the thing.
          className="h-full w-full object-contain"
        />
      )}
    </div>
  );
}
